import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import {
  ArrowLeft,
  Edit,
  Star,
  Package,
  DollarSign,
  ShoppingCart,
  Calendar,
  User,
  Clock,
  TrendingUp,
  AlertTriangle,
  CreditCard,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { AuthenticatedImage } from '@/components/AuthenticatedImage'
import { ProductImage } from '@/components/MultipleImageUpload'
import { apiClient } from '@/services/api'
import { useCurrentTemplate } from '@/hooks/useCurrentTemplate'

interface Product {
  id: string
  name: string
  type: string
  description?: string
  materialCost: number
  laborCost: number
  totalCost: number
  suggestedSellingPrice: number
  actualSellingPrice?: number
  profitMargin?: number
  totalLaborHours: number
  imageUrls: string[]
  images?: ProductImage[]
  isActive: boolean
  isFeatured: boolean
  timesReordered: number
  quantityInStock: number
  quantitySold: number
  lastSoldPrice?: number
  lastSoldDate?: string
  customerName?: string
  completedDate: string
  createdAt: string
}

export const ProductViewPage: React.FC = () => {
  const { template: currentTemplate } = useCurrentTemplate()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [product, setProduct] = useState<Product | null>(null)
  const [productImages, setProductImages] = useState<ProductImage[]>([])
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showSellModal, setShowSellModal] = useState(false)
  const [sellQuantity, setSellQuantity] = useState(1)
  const [sellPrice, setSellPrice] = useState('')
  const [showPurchaseModal, setShowPurchaseModal] = useState(false)

  useEffect(() => {
    if (id) {
      fetchProduct()
    }
  }, [id])

  const fetchProduct = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await apiClient.get(`/products/${id}`)
      console.log('🔍 Product API response:', response)
      setProduct(response)
      
      // Fetch product images using the new API
      try {
        const imagesResponse = await apiClient.get(`/products/${id}/images`)
        console.log('🖼️ Product images response:', imagesResponse)
        setProductImages(imagesResponse)
        
        // Set default image as selected, or first image if no default
        const defaultImageIndex = imagesResponse.findIndex((img: ProductImage) => img.isDefault)
        setSelectedImageIndex(defaultImageIndex >= 0 ? defaultImageIndex : 0)
      } catch (imageError) {
        console.warn('Failed to fetch product images, using fallback:', imageError)
        setProductImages([])
      }
    } catch (err) {
      console.error('❌ Error fetching product:', err)
      setError('Failed to load product')
    } finally {
      setIsLoading(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const getStockStatus = () => {
    if (!product) return { status: 'unknown', label: 'Unknown', color: 'text-gray-600', variant: 'secondary' as const }
    if (product.quantityInStock === 0) {
      return { status: 'out-of-stock', label: 'Out of Stock', color: 'text-red-600', variant: 'destructive' as const }
    }
    if (product.quantityInStock <= 2) {
      return { status: 'low-stock', label: 'Low Stock', color: 'text-yellow-600', variant: 'warning' as const }
    }
    return { status: 'in-stock', label: 'In Stock', color: 'text-green-600', variant: 'success' as const }
  }

  const handleSellProduct = async (quantity: number, price: number) => {
    if (!product) return
    
    try {
      const response = await apiClient.post(`/products/${product.id}/mark-sold`, {
        quantity,
        salePrice: price,
        saleDate: new Date().toISOString()
      })
      
      // Update local state
      setProduct(response)
      
      // Reset modal state
      setShowSellModal(false)
      setSellQuantity(1)
      setSellPrice('')
    } catch (error) {
      console.error('Error selling product:', error)
    }
  }

  const handleReorder = async () => {
    if (!product) return
    
    try {
      const response = await apiClient.post(`/products/${product.id}/reorder`, {
        quantity: 1,
        notes: 'Reordered from product view'
      })
      
      // Update local state
      setProduct(prev => prev ? { ...prev, timesReordered: prev.timesReordered + 1 } : null)
      
      // Show availability info if there are missing materials
      if (!response.availability?.available) {
        alert(`Cannot complete reorder. Missing materials: ${response.availability?.missingMaterials?.map((m: any) => m.materialName).join(', ') || 'Unknown'}`)
      } else {
        alert('Product reordered successfully!')
      }
    } catch (error) {
      console.error('Error reordering product:', error)
      alert('Error reordering product. Please try again.')
    }
  }

  const handlePurchaseMaterials = () => {
    // This would show a modal for purchasing materials needed for this product
    setShowPurchaseModal(true)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Package className="w-12 h-12 mx-auto mb-4 text-gray-400 animate-pulse" />
          <p className="text-gray-600 dark:text-gray-400">Loading product...</p>
        </div>
      </div>
    )
  }

  if (error || !product) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-400" />
          <p className="text-red-600 dark:text-red-400">{error || 'Product not found'}</p>
          <Button
            variant="outline"
            onClick={() => navigate('/products')}
            className="mt-4"
            leftIcon={<ArrowLeft className="w-4 h-4" />}
          >
            Back to Products
          </Button>
        </div>
      </div>
    )
  }

  const stockStatus = getStockStatus()

  return (
    <>
      <Helmet>
        <title>{product.name} - Product Details - {currentTemplate?.name || 'Business'} Tracker</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/products')}
              leftIcon={<ArrowLeft className="w-4 h-4" />}
            >
              Back to Products
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {product.name}
              </h1>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="secondary">{product.type}</Badge>
                {product.isFeatured && (
                  <Badge variant="warning">
                    <Star className="w-3 h-3 mr-1" />
                    Featured
                  </Badge>
                )}
                {!product.isActive && (
                  <Badge variant="secondary">Inactive</Badge>
                )}
                <Badge variant={stockStatus.variant}>
                  {stockStatus.label}
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowSellModal(true)}
              disabled={product.quantityInStock === 0}
              leftIcon={<DollarSign className="w-4 h-4" />}
              className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            >
              Sell Product
            </Button>
            <Button
              variant="outline"
              onClick={handlePurchaseMaterials}
              leftIcon={<CreditCard className="w-4 h-4" />}
              className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            >
              Purchase Materials
            </Button>
            <Button
              variant="outline"
              onClick={handleReorder}
              leftIcon={<ShoppingCart className="w-4 h-4" />}
              className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            >
              Reorder
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/products/${id}/edit`)}
              leftIcon={<Edit className="w-4 h-4" />}
              className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            >
              Edit Product
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Product Image Gallery */}
          <div className="lg:col-span-1">
            <Card className="p-6">
              {/* Main Image Display */}
              <div className="aspect-square bg-gray-100 dark:bg-gray-800 rounded-lg mb-4 relative overflow-hidden">
                {productImages.length > 0 ? (
                  <>
                    <AuthenticatedImage
                      src={productImages[selectedImageIndex]?.imageUrl}
                      alt={productImages[selectedImageIndex]?.altText || product.name}
                      className="w-full h-full object-cover rounded-lg relative z-0"
                      fallback={
                        <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg">
                          <Package className="w-16 h-16 text-gray-400" />
                        </div>
                      }
                    />
                    {/* Default Image Badge */}
                    {productImages[selectedImageIndex]?.isDefault && (
                      <div className="absolute top-3 left-3 bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                        <Star className="w-3 h-3 fill-current" />
                        Default
                      </div>
                    )}
                    {/* Navigation Arrows for Multiple Images */}
                    {productImages.length > 1 && (
                      <>
                        <button
                          onClick={() => setSelectedImageIndex(prev => prev > 0 ? prev - 1 : productImages.length - 1)}
                          className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setSelectedImageIndex(prev => prev < productImages.length - 1 ? prev + 1 : 0)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </>
                ) : product.imageUrls.length > 0 ? (
                  // Fallback to old imageUrls system
                  <AuthenticatedImage
                    src={product.imageUrls[0]}
                    alt={product.name}
                    className="w-full h-full object-cover rounded-lg relative z-0"
                    fallback={
                      <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg">
                        <Package className="w-16 h-16 text-gray-400" />
                      </div>
                    }
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg">
                    <Package className="w-16 h-16 text-gray-400" />
                  </div>
                )}
              </div>

              {/* Image Thumbnails */}
              {productImages.length > 1 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Images ({productImages.length})
                    </h4>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {selectedImageIndex + 1} of {productImages.length}
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2 max-h-32 overflow-y-auto">
                    {productImages.map((image, index) => (
                      <button
                        key={image.id}
                        onClick={() => setSelectedImageIndex(index)}
                        className={`
                          aspect-square bg-gray-100 dark:bg-gray-800 rounded relative overflow-hidden border-2 transition-colors
                          ${selectedImageIndex === index
                            ? 'border-blue-500 dark:border-blue-400'
                            : 'border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                          }
                        `}
                      >
                        <AuthenticatedImage
                          src={image.imageUrl}
                          alt={image.altText || `${product.name} ${index + 1}`}
                          className="w-full h-full object-cover rounded"
                          fallback={
                            <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded">
                              <Package className="w-6 h-6 text-gray-400" />
                            </div>
                          }
                        />
                        {/* Default indicator on thumbnail */}
                        {image.isDefault && (
                          <div className="absolute top-1 right-1">
                            <Star className="w-3 h-3 text-yellow-500 fill-current" />
                          </div>
                        )}
                        {/* Selected indicator */}
                        {selectedImageIndex === index && (
                          <div className="absolute inset-0 bg-blue-500/20 rounded" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Fallback for old imageUrls system */}
              {productImages.length === 0 && product.imageUrls.length > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {product.imageUrls.slice(1, 5).map((url, index) => (
                    <div key={index} className="aspect-square bg-gray-100 dark:bg-gray-800 rounded relative overflow-hidden">
                      <AuthenticatedImage
                        src={url}
                        alt={`${product.name} ${index + 2}`}
                        className="w-full h-full object-cover rounded relative z-0"
                        fallback={
                          <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded">
                            <Package className="w-8 h-8 text-gray-400" />
                          </div>
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Product Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Product Information</h2>
              {product.description && (
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {product.description}
                </p>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Product Type</label>
                  <p className="text-gray-900 dark:text-white">{product.type}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Completed Date</label>
                  <p className="text-gray-900 dark:text-white">{formatDate(product.completedDate)}</p>
                </div>
                {product.customerName && (
                  <div>
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Customer</label>
                    <p className="text-gray-900 dark:text-white">{product.customerName}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Labor Hours</label>
                  <p className="text-gray-900 dark:text-white">{product.totalLaborHours} hours</p>
                </div>
              </div>
            </Card>

            {/* Inventory Status */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Inventory Status</h2>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className={`text-2xl font-bold ${stockStatus.color}`}>
                    {product.quantityInStock}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">In Stock</div>
                </div>
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {product.quantitySold}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Total Sold</div>
                </div>
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {product.timesReordered}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Times Reordered</div>
                </div>
              </div>
              {product.lastSoldDate && (
                <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Last Sale:</span>
                    <div className="text-right">
                      <div className="font-medium">{formatCurrency(product.lastSoldPrice!)}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {formatDate(product.lastSoldDate)}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Card>

            {/* Financial Information */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Financial Details</h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Material Cost:</span>
                  <span className="font-medium">{formatCurrency(product.materialCost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Labor Cost:</span>
                  <span className="font-medium">{formatCurrency(product.laborCost)}</span>
                </div>
                <div className="flex justify-between border-t pt-3">
                  <span className="text-gray-600 dark:text-gray-400">Total Cost:</span>
                  <span className="font-semibold">{formatCurrency(product.totalCost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Suggested Price:</span>
                  <span className="font-medium">{formatCurrency(product.suggestedSellingPrice)}</span>
                </div>
                {product.actualSellingPrice && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Actual Selling Price:</span>
                    <span className="font-semibold text-green-600">{formatCurrency(product.actualSellingPrice)}</span>
                  </div>
                )}
                {product.profitMargin && (
                  <div className="flex justify-between border-t pt-3">
                    <span className="text-gray-600 dark:text-gray-400">Profit Margin:</span>
                    <span className="font-semibold text-green-600">{Number(product.profitMargin || 0).toFixed(1)}%</span>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Sell Product Modal */}
      {showSellModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Sell Product</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Quantity</label>
                <Input
                  type="number"
                  min="1"
                  max={product.quantityInStock}
                  value={sellQuantity}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSellQuantity(parseInt(e.target.value) || 1)}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Available: {product.quantityInStock} units
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Sale Price</label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={sellPrice}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSellPrice(e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Suggested: {formatCurrency(product.suggestedSellingPrice)}
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowSellModal(false)
                    setSellQuantity(1)
                    setSellPrice('')
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (sellPrice) {
                      handleSellProduct(sellQuantity, parseFloat(sellPrice))
                    }
                  }}
                  disabled={!sellPrice}
                  className="flex-1"
                >
                  Sell
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}